import type { AssistantSuggestionView, IntakePatch, IntakeView } from '@concord/domain/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';

export const intakeKey = (id: string) => ['intake', id] as const;

export function useIntake(id: string) {
  return useQuery({ queryKey: intakeKey(id), queryFn: () => api<IntakeView>(`/cases/${id}/intake`) });
}

export function usePatchIntake(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: IntakePatch) =>
      api<{ ok: true; savedAt: string }>(`/cases/${id}/intake`, { method: 'PATCH', body: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: intakeKey(id) }),
  });
}

export type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; at: Date }
  | { kind: 'error'; message: string };

/**
 * Autosave for free text (INT-05): saves after a pause in typing and on unmount, and
 * reports its state so the screen can say "Saved just now".
 */
export function useAutosave(id: string, field: 'history', initial: string, delay = 800) {
  const [value, setValue] = useState(initial);
  const [state, setState] = useState<SaveState>({ kind: 'idle' });
  const pending = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const v = pending.current;
    if (v === null) return;
    pending.current = null;
    setState({ kind: 'saving' });
    try {
      await api(`/cases/${id}/intake`, { method: 'PATCH', body: { [field]: v } });
      setState({ kind: 'saved', at: new Date() });
    } catch (e) {
      pending.current = v;
      setState({ kind: 'error', message: e instanceof Error ? e.message : 'Not saved' });
    }
  }, [id, field]);

  const change = useCallback(
    (v: string) => {
      setValue(v);
      pending.current = v;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void flush(), delay);
    },
    [flush, delay],
  );

  useEffect(() => () => void flush(), [flush]);
  return { value, change, state, flush };
}

export function useSetTimes(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { tz: string; slots: { date: string; time: string }[] }) =>
      api(`/cases/${id}/times`, { method: 'PUT', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: intakeKey(id) }),
  });
}

export function useSubmit(id: string) {
  return useMutation({
    mutationFn: (termsVersion: string) =>
      api<{ reference: string }>(`/cases/${id}/submit`, { body: { termsVersion, accepted: true } }),
  });
}

export async function askAssistant(id: string, history: string) {
  return (
    await api<{ suggestion: AssistantSuggestionView | null }>(`/cases/${id}/assistant`, { body: { history } })
  ).suggestion;
}

export async function resolveSuggestion(id: string, sid: string, outcome: 'accepted' | 'edited' | 'skipped') {
  await api(`/cases/${id}/assistant/${sid}`, { body: { outcome } });
}

export async function searchCaseTypes(q: string, caseId: string) {
  return (
    await api<{ items: { id: string; term: string; synonyms: string[] }[] }>(
      `/catalog/case-types?q=${encodeURIComponent(q)}&caseId=${encodeURIComponent(caseId)}`,
    )
  ).items;
}

/**
 * Upload with real progress (DOC-01): create the record, then PUT the bytes. XHR because
 * fetch cannot report upload progress.
 */
export async function uploadRecord(
  caseId: string,
  file: File,
  onProgress: (loaded: number, total: number) => void,
  signal: AbortSignal,
): Promise<{ id: string }> {
  const { id, uploadUrl } = await api<{ id: string; uploadUrl: string }>(`/cases/${caseId}/attachments`, {
    body: { filename: file.name, sizeBytes: file.size, contentType: file.type || 'application/octet-stream' },
  });
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('content-type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => onProgress(e.loaded, e.total || file.size);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      let message = `Upload failed (${xhr.status}).`;
      try {
        message = (JSON.parse(xhr.responseText) as { error: { message: string } }).error.message;
      } catch {
        // keep the generic message
      }
      reject(new Error(message));
    };
    xhr.onerror = () => reject(new Error('Upload interrupted. Check your connection and try again.'));
    signal.addEventListener('abort', () => xhr.abort());
    xhr.onabort = () => reject(new DOMException('aborted', 'AbortError'));
    xhr.send(file);
  });
  return { id };
}
